import React, { useEffect, useMemo, useRef } from 'react'
import { Box, Flex, Text, useAppTheme } from '@workwhile/ui'
import { AccordionCard } from 'components/AccordionCard'
import {
  useVoiceAgentCallContextValue,
  type TranscriptEntry,
} from '../context/VoiceAgentCallContext'

type MergedEntry = {
  role: 'user' | 'assistant'
  text: string
  isFinal: boolean
  timestamp: number
}

const mergeTranscript = (entries: TranscriptEntry[]): MergedEntry[] => {
  const merged: MergedEntry[] = []

  for (const entry of entries) {
    const last = merged[merged.length - 1]
    if (last && last.role === entry.role) {
      const separator = last.isFinal ? ' ' : ''
      last.text = last.isFinal ? last.text + separator + entry.text : entry.text
      last.isFinal = entry.isFinal
      last.timestamp = entry.timestamp
    } else {
      merged.push({ ...entry })
    }
  }

  return merged
}

export const Transcript = () => {
  const {
    state: { transcript, callId },
  } = useVoiceAgentCallContextValue()
  const { colors } = useAppTheme()
  const bottomRef = useRef<HTMLDivElement>(null)
  const merged = useMemo(() => mergeTranscript(transcript), [transcript])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [merged])

  if (merged.length === 0) {
    return (
      <AccordionCard
        title="Transcript"
        contentWrapperProps={{ style: { maxHeight: 400, overflowY: 'auto' } }}
      >
        <Text>Transcript will appear here when the call is active</Text>
      </AccordionCard>
    )
  }

  return (
    <AccordionCard
      title={
        <Flex flexDirection="column" gap={2}>
          <Text fontSize={2} color="lightText">
            Transcript
          </Text>
          {callId ? (
            <Text fontSize={0} color="lightText">
              Call ID: {callId}
            </Text>
          ) : null}
        </Flex>
      }
      contentWrapperProps={{ style: { maxHeight: 400, overflowY: 'auto' } }}
    >
      {merged.map((entry, i) => (
        <Flex
          key={`${entry.timestamp}-${i}`}
          mb={2}
          style={{
            justifyContent: entry.role === 'user' ? 'flex-end' : 'flex-start',
          }}
        >
          <Box
            p={2}
            style={{
              maxWidth: '80%',
              borderRadius: 16,
              backgroundColor:
                entry.role === 'user' ? colors.intelligence[600] : colors.white,
              border: '1px solid',
              borderColor:
                entry.role === 'user' ? 'transparent' : colors.neutrals[100],
            }}
          >
            {entry.role === 'assistant' && (
              <Text fontSize={0} fontWeight="bold" color="lightText" mb={1}>
                Assistant
              </Text>
            )}
            <Text
              fontSize={1}
              color={entry.role === 'user' ? 'white' : 'black'}
            >
              {entry.text}
            </Text>
          </Box>
        </Flex>
      ))}
      <div ref={bottomRef} />
    </AccordionCard>
  )
}
